import { Log } from '../../AuxillaryService/Log';
import JSZip from 'jszip';
import { tryFetchMetadata } from './tryFetchMetadata';
import { isNullish } from '../../Utility/Nullish';
import mime from 'mime-types';
import { asyncTimeout } from '../../Utility/AsyncTimeout';
import { isNotBlank } from '../../Utility/Blank';

/**
 * Reference to the "sites" browser cache.
 */
let cacheInstance: Cache | undefined = undefined;

/**
 * Opens the browser "sites" cache where we will store our cached zip and zip files for IFrame
 * Run From Zip configurations.
 */
async function openCache(): Promise<Cache> {
    if (undefined === cacheInstance) {
        cacheInstance = await caches.open('sites');
    }

    return cacheInstance;
}

/**
 * Returns true if the zip file located at `originUrl` is already in the browser cache.
 */
export async function isSiteZipInCache(originUrl: string): Promise<boolean> {
    const encodedOriginUrl = encodeURIComponent(originUrl);
    const cachedZipFileUrl = `https://sites.reach.local/${encodedOriginUrl}`;

    const cache = await openCache();

    return (await cache.match(cachedZipFileUrl)) !== undefined;
}

/**
 * Attempts to download the presumptive zip file at `originalUrl` into the browser Cache API.
 */
export async function tryCacheSiteZipFile(originUrl: string): Promise<void> {
    const encodedOriginUrl = encodeURIComponent(originUrl);

    Log.Debug(`Fetching IFrame source zip from '${originUrl}'`, null, __fileline);

    const resp = await fetch(originUrl);
    const blob = await resp.blob();

    //attempt to load the zip using JSZip to validate that this file is a zip file
    Log.Debug(`Fetch succeeded, reading zip file data from '${originUrl}'`, null, __fileline);
    await JSZip.loadAsync(blob);

    //make HEAD request to get etag and last-modified headers
    let { etag, lastModified } = await tryFetchMetadata(originUrl);

    Log.Debug(`Result of HEAD request to ${originUrl}`, null, __fileline, {
        etag,
        lastModified,
    });

    const cache = await openCache();

    //metadata fetch failed, use etag and last-modified from the GET request instead
    if (null === etag && null === lastModified) {
        etag = etag ?? resp.headers.get('etag');
        lastModified = lastModified ?? resp.headers.get('last-modified');
    }

    const response = new Response(blob, {
        headers: {
            'Content-Type': 'application/zip',
            'Content-Length': blob.size.toString(),
            ...(isNullish(etag) ? {} : { ETag: etag }),
            ...(isNullish(lastModified) ? {} : { 'Last-Modified': lastModified }),
        },
    });
    const request = new Request(`https://sites.reach.local/${encodedOriginUrl}`);

    await cache.put(request, response);
}

/**
 * loads the zip file from cache for `originUrl` and extracts the contents into the browser cache
 * with a base URI of https://sites.reach.local/<url-encoded-origin-url>/.
 */
export async function extractZipToCache(originUrl: string): Promise<void> {
    const encodedOriginUrl = encodeURIComponent(originUrl);
    const cachedZipFileUrl = `https://sites.reach.local/${encodedOriginUrl}`;

    const cache = await openCache();

    const cacheResponse = await cache.match(cachedZipFileUrl);

    if (undefined === cacheResponse) {
        throw new Error(`Failed to fetch cache zip file '${cachedZipFileUrl}'`);
    }

    const zip = await JSZip.loadAsync(cacheResponse.blob());

    let itemsCached = 0;
    let bytesCached = 0;
    const startedAt = Date.now();

    for (const [path, entry] of Object.entries(zip.files)) {
        if (entry.dir) {
            continue;
        }

        const content = await entry.async('blob');
        const mimetype = mime.lookup(path);
        const response = new Response(content, {
            headers: {
                'Content-Type': mimetype !== false ? mimetype : 'application/octet-stream',
                'Content-Length': content.size.toString(),
            },
        });

        const targetCacheUrl = new URL(
            path,
            `https://sites.reach.local/${encodedOriginUrl}/`
        ).toString();
        const request = new Request(targetCacheUrl);

        Log.Debug(
            `Committing zip file relative path '${path}' to '${targetCacheUrl}'`,
            null,
            __fileline
        );

        await cache.put(request, response);

        ++itemsCached;
        bytesCached += content.size;
    }

    const elapsed = Date.now() - startedAt;

    Log.Debug(
        `Cached ${itemsCached} item(s) consisting of ${bytesCached} byte(s) in ${elapsed} msec(s)`,
        null,
        __fileline
    );
}

/**
 * downloads the presumptive zip file located at `decodedOriginUrl` into the browser cache and then
 * extracts the contents into the browser cache with a base URI of
 * https://sites.reach.local/<url-encoded-origin-url>/.
 */
export async function cacheRunFromZipSite(decodedOriginUrl: string): Promise<void> {
    for (let i = 0; i < 5; ++i) {
        if (i > 0) {
            await asyncTimeout(5000);
        }

        try {
            await tryCacheSiteZipFile(decodedOriginUrl);

            await extractZipToCache(decodedOriginUrl);

            return;
        } catch (err) {
            Log.Warning(
                `Failed to cache site zip file for URL '${decodedOriginUrl}'`,
                null,
                __fileline,
                err
            );
        }
    }

    throw new Error('failed to cache site');
}

/**
 * Deletes the zip file (and it's related resources) from cache for `decodedOriginUrl`.
 */
export async function deleteRunFromZipSite(decodedOriginUrl: string): Promise<void> {
    const cache = await openCache();
    const targetCacheKeyPrefix = `https://sites.reach.local/${encodeURIComponent(
        decodedOriginUrl
    )}`;

    let itemsDeleted = 0;
    const startedAt = Date.now();

    for (const key of await cache.keys()) {
        if (key.url.startsWith(targetCacheKeyPrefix)) {
            ++itemsDeleted;

            await cache.delete(key.url);
        }
    }

    const elapsed = Date.now() - startedAt;

    Log.Debug(`Deleted ${itemsDeleted} item(s) from cache in ${elapsed} msec(s)`, null, __fileline);
}

/**
 * Checks remote server to see if the hosted zip file is changed from what we have in cache.
 * Returns `true` if the zip file in cache (and related files) are updated, `false` if no update
 * was performed.
 */
export async function updateCachedRunFromZipSite(
    decodedOriginUrl: string,
    forceUpdate = false
): Promise<boolean> {
    const encodedOriginUrl = encodeURIComponent(decodedOriginUrl);
    let shouldPerformUpdate = false;

    //fetch HEAD response from remote server
    const { etag, lastModified } = await tryFetchMetadata(decodedOriginUrl);

    if (!forceUpdate && (isNotBlank(etag) || isNotBlank(lastModified))) {
        //fetch cache entry
        const cache = await openCache();
        const cacheEntry = await cache.match(`https://sites.reach.local/${encodedOriginUrl}`);

        if (!isNullish(cacheEntry)) {
            const cacheEntryEtag = cacheEntry.headers.get('etag');
            const cacheEntryLastModified = cacheEntry.headers.get('last-modified');

            shouldPerformUpdate =
                etag !== cacheEntryEtag && lastModified !== cacheEntryLastModified;

            Log.Debug(
                `re-cache decision for URL ${decodedOriginUrl}; shouldPerformUpdate=${String(
                    shouldPerformUpdate
                )}, server ETag=${String(etag)}, cache ETag=${String(
                    cacheEntryEtag
                )}, server Last-Modified=${String(lastModified)}, cache Last-Modified=${String(
                    cacheEntryLastModified
                )}`,
                null,
                __fileline
            );
        } else {
            //entry not in cache, perform update.
            shouldPerformUpdate = true;
        }
    } else {
        //we failed to get metadata details from HEAD request, so we'll just assume the archive has
        //changed and do an update.
        shouldPerformUpdate = true;
    }

    if (forceUpdate || shouldPerformUpdate) {
        await deleteRunFromZipSite(decodedOriginUrl);

        await cacheRunFromZipSite(decodedOriginUrl);

        return true;
    }

    return false;
}
